import { FC } from "react";
import { Link } from "react-router-dom";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import Hero from "../../components/Hero";
import CtaSection from "../../components/CtaSection";
import { globalContent } from "../../config/content";
import styles from "./About.module.scss";

const About: FC = () => {
  return (
    <div className={styles.aboutPage}>
      {/* 1. Hero */}
      <Hero
        eyebrow="About Pulse"
        heading="Building information should be easier to use."
        copy="Buildings generate an enormous amount of information. Drawings, documents, certificates, asset data, maintenance records and more.

Pulse was created to make that information easier to organise, access and use."
        backgroundImage={require("../../assets/images/building/about-building-information.webp")}
        button={{
          text: "View the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/request-a-demo",
          variant: "outlineLight",
        }}
      />

      {/* 2. Why Pulse exists */}
      <Section bgColor="off-white">
        <div className={styles.textLeadSection}>
          <span className={styles.eyebrow}>Why Pulse Exists</span>
          <h2 className={styles.sectionHeading}>
            Making sense of complex building information.
          </h2>
          <p className={styles.sectionCopy}>
            Building information is often spread across folders, drawings, PDFs,
            spreadsheets and systems.
          </p>
          <p className={styles.sectionCopy}>
            The information may all exist, but finding the right information
            when it is needed can still be difficult.
          </p>
          <p className={styles.sectionCopy}>
            Pulse provides a structured environment where building information
            can be brought together and accessed from one place.
          </p>
        </div>
      </Section>

      {/* 3. Our approach */}
      <Section bgColor="white">
        <div
          className={styles.textLeadSection}
          style={{ marginBottom: "2rem" }}
        >
          <span className={styles.eyebrow}>Our Approach</span>
          <h2 className={styles.sectionHeading}>
            A practical approach to building information.
          </h2>
        </div>
        <div className={styles.cardsGrid}>
          <div className={styles.cardItem}>
            <h3>Structured</h3>
            <p>
              Keep building information organised around the building, its
              assets and the documentation associated with them.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Accessible</h3>
            <p>
              Make important information available to the people who need it,
              whether they are at a desk or working in the building.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Secure</h3>
            <p>
              Provide controlled access to important building information in a
              secure environment.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Useful</h3>
            <p>
              Use technology to make building information easier to find,
              understand and work with.
            </p>
          </div>
        </div>
      </Section>

      {/* 4. Built around real buildings */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Real-World Environment</span>
            <h2 className={styles.sectionHeading}>
              From the building to the information.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse is designed around the relationship between a building and
              the information that helps people manage it.
            </p>
            <p className={styles.sectionCopy}>
              Documents, drawings, assets and other building information are
              brought together in one structured environment, making them easier
              to navigate and use.
            </p>
            <p className={styles.sectionCopy}>
              Whether someone is working at a desk or accessing information
              while in the building, Pulse is designed to put the information
              they need within reach.
            </p>
            <div className={styles.actionLink}>
              <Link
                to="/digital-building-information"
                className={styles.textLink}
              >
                Explore Digital Building Information →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/product/about-pulse-in-use.webp")}
              alt="Pulse in use in a real building environment"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 5. Looking ahead */}
      <Section bgColor="white">
        <div className={styles.textLeadSection}>
          <span className={styles.eyebrow}>Looking Ahead</span>
          <h2 className={styles.sectionHeading}>
            Built to evolve with your building.
          </h2>
          <p className={styles.sectionCopy}>
            Buildings change. Their information changes with them.
          </p>
          <p className={styles.sectionCopy}>
            New equipment is installed. Spaces are altered. Documents are
            updated. Maintenance information grows.
          </p>
          <p className={styles.sectionCopy}>
            Pulse is being developed to make building information increasingly
            accessible, searchable and useful as buildings and the people
            responsible for them evolve.
          </p>
        </div>
      </Section>

      {/* 6. Final CTA */}
      <CtaSection
        eyebrow="Get Started"
        heading="See what Pulse can do for your building."
        copy="Discover how Pulse can bring your building information together and make it easier for your teams to find, access and use."
        button={{
          text: "Request a Demo",
          to: "/request-a-demo",
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Explore Pulse",
          to: "/digital-building-information",
          variant: "outlineLight",
        }}
      />
    </div>
  );
};

export default withPage(About)({
  meta: globalContent.seo.about,
});
